import EmblaCarousel, {EmblaCarouselType, EngineType} from "embla-carousel";
import ClassNames from "embla-carousel-class-names";

const clamp = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
}

type TCarouselOptions = {
    prevButton: HTMLButtonElement;
    nextButton: HTMLButtonElement;
    showDots?: boolean;
    dotsContainer?: HTMLElement;
}

export class Carousel {
    private _sliderEl: HTMLElement;
    private _emblaCarousel: EmblaCarouselType;

    private _prevButton: HTMLButtonElement;
    private _nextButton: HTMLButtonElement;

    private _currentDot: HTMLButtonElement;
    private _dots: HTMLButtonElement[];

    private _updateHandle: () => void;

    constructor(sliderEl: HTMLElement, options: TCarouselOptions) {
        this._sliderEl = sliderEl;

        this._prevButton = options.prevButton;
        this._nextButton = options.nextButton;

        this._emblaCarousel = EmblaCarousel(sliderEl, {
            align: "center",
            containScroll: false
        }, [ClassNames()]);

        this._updateHandle = this._update.bind(this);

        if (options.showDots) {
            if (!options.dotsContainer) {
                throw new Error("Dots container is null");
            }

            this._addDots(options.dotsContainer);
        }

        this._tweenSlides();
        this._update();
        this._addEventListeners();
    }

    private _addEventListeners(): void {
        this._emblaCarousel.on("scroll", () => {
            this._tweenSlides();
        });

        this._emblaCarousel.on("select", this._updateHandle);
        this._emblaCarousel.on("init", this._updateHandle);
        this._emblaCarousel.on("reInit", this._updateHandle);

        this._prevButton.addEventListener("click", () => {
            this._emblaCarousel.scrollPrev();
        });

        this._nextButton.addEventListener("click", () => {
            this._emblaCarousel.scrollNext();
        });
    }

    private _tweenSlides() {
        const scrollProgress = this._emblaCarousel.scrollProgress();
        const snapList = this._emblaCarousel.scrollSnapList();

        snapList.forEach((scrollSnap, snapIndex) => {
            const slide = this._emblaCarousel.slideNodes()[snapIndex];

            let diffToTarget = Math.abs(scrollSnap - scrollProgress);

            const opacityValue = clamp(1 - diffToTarget * 10, 0.4, 1);
            const scaleValue = clamp(1 - diffToTarget, 0.875, 1);

            // slide.style.zIndex = 1 - diffToTarget > 0.9 ? "1" : "0";
            slide.style.opacity = opacityValue.toString();
            slide.style.transform = `scale(${scaleValue})`;
        });
    }

    private _addDots(container: HTMLElement) {
        const slidesCount = this._emblaCarousel.slideNodes().length;

        if (slidesCount < 2) {
            return;
        }

        this._dots = [];

        for (let i = 0; i < slidesCount; i++) {
            const dot = document.createElement("button");

            dot.className = "ao-slider__dot";

            container.appendChild(dot);

            this._dots.push(dot);
        }

        this._updateCurrentDot();
    }

    private _update() {
        this._updateCurrentDot();
        this._updateButtons();
    }

    private _updateCurrentDot() {
        if (!this._dots) return;

        const activeSlideIndex = this._emblaCarousel.selectedScrollSnap();
        const dot = this._dots[activeSlideIndex];

        if (dot) {
            if (this._currentDot) {
                this._currentDot.classList.remove("ao-slider__dot--current");
            }

            dot.classList.add("ao-slider__dot--current");

            this._currentDot = dot;
        }
    }

    private _updateButtons() {
        this._prevButton.disabled = !this._emblaCarousel.canScrollPrev();
        this._nextButton.disabled = !this._emblaCarousel.canScrollNext();
    }
}